import { useCallback, useMemo, useState } from "react";

// Normaliza a timestamp numérico: Date → getTime, número → tal cual, string →
// Date.parse. Devuelve NaN si no es una fecha válida (lo captura isEmpty).
function toTimestamp(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return Date.parse(value);
}

// Un valor es "vacío/placeholder" si no aporta un dato ordenable real. Cubre
// sin folio (null / "" / "-"), sin fecha, montos ausentes y fechas no parseables.
function isEmpty(value, type) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (type === "number" && Number.isNaN(Number(value))) return true;
  if (type === "date" && Number.isNaN(toTimestamp(value))) return true;
  return false;
}

// Comparación base ASCENDENTE por tipo (la dirección la aplica el caller).
function compareByType(a, b, type) {
  switch (type) {
    case "number":
      return Number(a) - Number(b);
    case "date":
      return toTimestamp(a) - toTimestamp(b);
    case "text-numeric":
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    case "text":
    default:
      return String(a).localeCompare(String(b));
  }
}

/**
 * Hook genérico de ordenamiento de tablas por click en el header.
 *
 * Generaliza el patrón ya probado en EdificiosTable (sortConfig {key,dir} +
 * ciclo de 3 estados + indicador ▲/▼) para que sirva a CUALQUIER tabla del
 * proyecto sin reinventarlo en cada componente (reglas 10.1 y 10.27). El hook
 * NO conoce ninguna columna concreta: todo el conocimiento de qué ordenar y
 * cómo entra por `columnDefs`.
 *
 * Ciclo por click sobre la MISMA columna: asc → desc → null. En `null` no
 * ordena y `sortRows` devuelve las filas tal cual las recibió, respetando el
 * orden natural que haya armado el caller.
 *
 * `columnDefs` es un mapa `{ [key]: colDef }` donde cada `colDef` declara:
 *   - `accessor(row)`  → cómo sacar el valor comparable de la fila.
 *   - `type`           → 'text' | 'text-numeric' | 'number' | 'date'.
 *   - `nulls`          → 'first' | 'last' (opcional, default 'last'): a qué
 *                        extremo van los vacíos/placeholders. Los vacíos quedan
 *                        SIEMPRE en el mismo extremo, independientemente de si
 *                        el orden es asc o desc (patrón estándar: evita que el
 *                        set mixto quede inconsistente al invertir la dirección).
 *
 * Tipos soportados:
 *   - 'text'         → localeCompare (EJECUTIVO, CLIENTE, INTERMEDIARIO…).
 *   - 'text-numeric' → localeCompare con { numeric:true } (Nº FACTURA, que
 *                       mezcla dígitos y strings tipo "F-123" / "1024-A").
 *   - 'number'       → resta numérica (VENTA, COMISIÓN %, COMISIÓN $, NETO;
 *                       reciben un escalar ya precalculado por el caller).
 *   - 'date'         → comparación por timestamp (FECHA FACTURA, COBRO; el
 *                       accessor devuelve un timestamp/Date/fecha parseable).
 *
 * El orden es ESTABLE: entre filas equivalentes se conserva el orden original
 * (desempate por índice), tanto en asc como en desc y entre vacíos del mismo
 * extremo.
 *
 * @param {Object} columnDefs - Mapa key→colDef. Para no recomputar en cada
 *                              render, definilo como constante de módulo o
 *                              memoizalo en el caller (su identidad es una
 *                              dependencia de `sortRows`).
 * @returns {{ sortConfig: {key: string|null, dir: 'asc'|'desc'|null},
 *             handleSort: (key: string) => void,
 *             sortIndicator: (key: string) => string,
 *             sortRows: (rows: Array) => Array }}
 */
export function useTableSort(columnDefs) {
  const [sortConfig, setSortConfig] = useState({ key: null, dir: null });

  // Ciclo de 3 estados por click sobre la misma columna: asc → desc → null.
  // Al cambiar de columna, arranca en asc (igual que EdificiosTable).
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  }, []);

  // Indicador para el header: ▲ / ▼ / vacío según el estado de esa columna.
  const sortIndicator = useCallback(
    (key) => {
      if (sortConfig.key !== key) return "";
      return sortConfig.dir === "asc" ? " ▲" : " ▼";
    },
    [sortConfig]
  );

  const sortRows = useCallback(
    (rows) => {
      const { key, dir } = sortConfig;
      // Sin orden activo → devolver la referencia original: el caller conserva
      // su orden natural y no forzamos una copia innecesaria.
      if (!key || !dir) return rows;
      const colDef = columnDefs?.[key];
      if (!colDef || typeof colDef.accessor !== "function") return rows;
      if (!Array.isArray(rows)) return rows;

      const nullsFirst = colDef.nulls === "first";
      const factor = dir === "desc" ? -1 : 1;

      // Decorate-sort-undecorate con el índice original como desempate → orden
      // estable garantizado (no dependemos de la estabilidad del engine).
      const decorated = rows.map((row, index) => ({ row, index }));

      decorated.sort((a, b) => {
        const rawA = colDef.accessor(a.row);
        const rawB = colDef.accessor(b.row);
        const emptyA = isEmpty(rawA, colDef.type);
        const emptyB = isEmpty(rawB, colDef.type);

        // Vacíos a un extremo fijo, independiente de la dirección de orden.
        if (emptyA || emptyB) {
          if (emptyA && emptyB) return a.index - b.index; // ambos vacíos → estable
          return (emptyA ? 1 : -1) * (nullsFirst ? -1 : 1);
        }

        const cmp = compareByType(rawA, rawB, colDef.type);
        if (cmp !== 0) return factor * cmp;
        return a.index - b.index; // estabilidad entre equivalentes
      });

      return decorated.map((entry) => entry.row);
    },
    [sortConfig, columnDefs]
  );

  return useMemo(
    () => ({ sortConfig, handleSort, sortIndicator, sortRows }),
    [sortConfig, handleSort, sortIndicator, sortRows]
  );
}
